/**
 * useAdManager - orchestrates monetisation overlays over the feed lifecycle.
 *
 * It pauses the current video when an overlay appears, resumes it on close,
 * persists pacing via `adScheduler`, and exposes:
 *   - overlayType     : current overlay or null (drives rendering)
 *   - checkAndShowAd  : evaluate the policy now; returns true if an overlay is up
 *   - closeOverlay    : dismiss + start the cooldown (also bound to window.closeAd)
 */
import { useState, useRef, useCallback, useEffect } from 'preact/hooks';
import {
  AD_CONFIG,
  decideOverlay,
  getNextOverlayAt,
  scheduleNext,
  markFirstOverlayDone,
  bumpInterstitialsSinceAppopen,
  resetInterstitialsSinceAppopen,
} from '../lib/adScheduler.js';

export function useAdManager({
  onPauseCurrentVideo,
  onResumeCurrentVideo,
  enabled = true,
}) {
  const [overlayType, setOverlayType] = useState(null);
  const overlayRef = useRef(null);
  overlayRef.current = overlayType;

  const didPause = useRef(false);
  const callbacks = useRef({ onPauseCurrentVideo, onResumeCurrentVideo });
  callbacks.current = { onPauseCurrentVideo, onResumeCurrentVideo };

  const closeOverlay = useCallback(() => {
    setOverlayType(null);
    overlayRef.current = null;
    scheduleNext(AD_CONFIG.cooldownMs);
    if (didPause.current) {
      didPause.current = false;
      callbacks.current.onResumeCurrentVideo?.();
    }
  }, []);

  const showOverlay = useCallback((type) => {
    setOverlayType(type);
    overlayRef.current = type;
    didPause.current = !!callbacks.current.onPauseCurrentVideo?.();
    markFirstOverlayDone();
    if (type === 'interstitial') {
      bumpInterstitialsSinceAppopen();
    } else {
      resetInterstitialsSinceAppopen();
    }
  }, []);

  const checkAndShowAd = useCallback(() => {
    if (!enabled) return false;
    if (overlayRef.current) return true;
    const nextAt = getNextOverlayAt();
    if (nextAt === 0 || Date.now() < nextAt) return false;
    const decision = decideOverlay();
    if (decision) {
      showOverlay(decision);
      return true;
    }
    scheduleNext(AD_CONFIG.retryWhenNotReadyMs);
    return false;
  }, [showOverlay, enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    const nextAt = getNextOverlayAt();
    if (nextAt) {
      if (nextAt < Date.now() && !decideOverlay()) {
        scheduleNext(AD_CONFIG.firstShowDelayMs);
      }
    } else {
      scheduleNext(AD_CONFIG.firstShowDelayMs);
    }
    // The native interstitial dismisses by calling window.closeAd().
    window.closeAd = closeOverlay;
    checkAndShowAd();
    return () => {
      delete window.closeAd;
    };
  }, [closeOverlay, checkAndShowAd]);

  return { overlayType, checkAndShowAd, closeOverlay };
}
